import { ADDRESS_FIELD, dayjsWithTimezone } from '@healthhub/react-ui';

import type { BreadCrumbDetails, OptionsType } from '@shared/components';
import { APPOINTMENT_PATH } from '@shared/constants/paths';

import { YesNoAnswer } from '../types/appointment.types';

// Floor/Unit is blank-able across every appointment flow. AddressInput registers no required rule on
// it, so making it mandatory would only grey out the submit button with no asterisk or inline message
// to explain why. It is still format-validated by AddressInput. Building is hidden in AddressInput and
// no longer collected, so it is not part of this list.
export const ADDRESS_OPTIONAL_FIELDS = [ADDRESS_FIELD.FLOOR_UNIT];

export const SOURCE_SYSTEM = {
  CEQMS: 'CEQMS',
  NGEMR: 'NGEMR',
  OAS: 'OAS',
};

export const QUESTIONNAIRE_TYPE = {
  GENERAL: 'GENERAL',
  PAYMENT: 'PAYMENT',
  SPECIALTY: 'SPECIALTY',
};

export const TELECONSULT_CODES = [
  'CATAVID',
  'CONSULTVD',
  'GDHPROGVD',
  'HOMEVISVID',
  'Z CONSULT',
  'Z COUNSEL',
  'Z THERAPY',
  'TELECONS',
  'CATAPHN',
  'HOMEVISIT',
];

export const PRESUFFIX_TELECONSULT_CODES = ['PC', 'HV'];

export const PRESUFFIX_VIDEO_CONSULT_CODES = ['VC', 'VG'];

export const TODAY = dayjsWithTimezone();

// Lower bound for date-of-birth pickers to prevent selecting implausible dates (e.g. before year 1900).
export const MIN_DATE_OF_BIRTH = '1900-01-01T08:00:00.000Z';

export const APPOINTMENT_FORM_TYPE = {
  bookNew: 'Book a new Appointment',
  cancel: 'Cancel an Existing Appointment',
  reschedule: 'Change an Existing Appointment',
};

export const YES_NO_QUESTIONS: OptionsType[] = [
  {
    label: YesNoAnswer.yes,
    value: 'true',
  },
  {
    label: YesNoAnswer.no,
    value: 'false',
  },
];

export const NO_YES_QUESTIONS: OptionsType[] = [
  {
    label: YesNoAnswer.no,
    value: 'false',
  },
  {
    label: YesNoAnswer.yes,
    value: 'true',
  },
];

export const NUMBER_REGEX = /^\d+$/;

export const HTML_TAG_REGEX = /<\/?([a-z][a-z0-9]*)\b[^>]*>/i;

export const COMMENT_PHONE_NUMBER_REGEX = /\d{4} \d{4}/;

export const MIX_TEXT_AND_PHONE_NUMBER_REGEX = /or call <a[^>]*>/g;

export const NUMBER_ORDER_QUESTIONNAIRE_REGEX = /Q([1-9]\d?|100)\./;

export const REASON_CANCEL_APPOINTMENT = [
  {
    label: 'Patient is unwell',
    value: 'Patient is unwell',
  },
  {
    label: 'Patient has other activity',
    value: 'Patient has other activity',
  },
  {
    label: 'Parent is busy',
    value: 'Parent is busy',
  },
  {
    label: 'Follow up by private dentist',
    value: 'Follow up by private dentist',
  },
  {
    label: 'Others',
    value: 'Others',
  },
];

export const SNI_APPOINTMENT_CANCEL_REASONS = [
  {
    label: 'Patient is unwell',
    value: 'F',
  },
  {
    label: 'Patient has other activity',
    value: 'G',
  },
  {
    label: 'Parent is busy',
    value: 'H',
  },
  {
    label: 'Follow up by private dentist',
    value: 'I',
  },
  {
    label: 'Others',
    value: '9',
  },
];

export const STUDENT_DENTAL_CENTRE = 'School Dental Centre';

export const STUDENT_HEALTH_CENTRE = 'Student Health Centre';

export const NATIONAL_SKIN_CENTRE = 'National Skin Centre';

export const REQUEST_FORM_TEXT = 'submit a request online.';

export const REQUEST_FORM_TEXT_VARIANT = '<a href="[REQUESTFORMLINK]" class="link">submit a request online</a>.';

export const REQUEST_FORM_BOOK_NEXT_TEXT = "via the 'Book next appointment'";

export const DESCRIPTION_TEXT =
  ' We request your co-operation not to email or call for alternative slots unless you have an urgent clinical condition.';

export const EXCLUDE_ORGANIZATION_CODES = ['NUP', 'SHP', 'NHGP'];

export const EXCLUDE_MEDREFILL_CODES = ['YCH', 'JCH'];

export const MREG_AVAILABILIY = {
  actualised: 'actualised',
  pending: 'pending',
};

export const ENCOUNTER_STATUS = {
  NOSHOW: 'NOSHOW',
  READY: 'READY',
  STARTED: 'STARTED',
};

export const ENCOUNTER_STATUS_DISPLAY = {
  COMPLETED: 'Completed',
  MISSED: 'Missed',
  PENDING_ARRIVAL: 'Pending Arrival',
  READY: 'Ready',
  WAITING: 'Waiting',
};

export const VC_SUPPORT_STATUS = {
  SUPPORTED: 'supported',
  REQUEUE: 'requeue',
};

export const SCREENING_STATUSES = {
  failed: 'Failed',
  success: 'Success',
};

export const VISIT_TYPE = {
  hsgCc: '117023191',
  hsgHealthPlanDiscussion: '117100000',
  nhgFinancialCounselling: '117023178',
  nscDermatologyRevisit: '310900119',
  nscNonSubFirstConsultation: '310900118',
};

export const ERR_CODE_OAS_FIRST_TIME_PATIENT = 'E510029';

export const OAS_PROTECTION_MESSAGE = [
  'EAPPT_PROTECTION_MESSAGE_LINE1',
  'EAPPT_PROTECTION_MESSAGE_LINE2',
  'EAPPT_PROTECTION_MESSAGE_LINE3',
];

export const OAS_MEDICATION_MESSAGE = [
  'EAPPT_MEDICATION_MESSAGE_LINE1',
  'EAPPT_MEDICATION_MESSAGE_LINE2',
  'EAPPT_MEDICATION_MESSAGE_LINE3',
];

export const APPOINTMENT_BREADCRUMBS: BreadCrumbDetails[] = [
  {
    path: APPOINTMENT_PATH,
    title: 'Appointments',
  },
];

export const QUESTION_TEXT_RECORDS = new Map<string, string>();

export const FORM_PLACEHOLDERS = {
  CONTACT: '[CONTACTLINK]',
  REQUEST_FORM: '[REQUESTFORMLINK]',
  SDC_FORM: '[SDCFORM]',
  SHC_FORM: '[SHCFORM]',
  SUBMIT_REQUEST: '[SUBMIT_REQUEST]',
};

export const CONTACT_LINK = 'https://www.nuhs.edu.sg/i-want-to/Pages/contact-NUHS.aspx';
export const ZOOM_TEST_LINK = 'https://zoom.us/test';

export const HASC = 'HASC';
export const HEALTH_PLAN_DISCUSSION = 'Health Plan Discussion';

export const VC_ACUTE = 'VC_ACUTE';
export const VC_CHRONIC = 'VC_CHRONIC';

export const CLICK_HERE = '[CLICKHERE]';

export const AUNTOGEN_BOOKED_STATUS = 'Busy';

export const APPOINTMENT_FORM_SERVICE_CATEGORY_CODE = 'Appointment.Form.Services';

export const NUP_TELECONSULT_VISIT_ID = ['117100003'];

export const APPOINTMENT_HASHCICODE_SYSTEM_SETTING = 'Appointments.HasClinic.HciCodes';

export const APPOINTMENT_HAS_SLOTSELECTION_DAYSOFFSET_SYSTEM_SETTING =
  'Appointments.HAS.Configuration.SlotSelectionDaysOffset';

export const APPOINTMENT_CREATE_APPOINTMENT_NOTE_MESSAGE_SYSTEM_SETTING = 'Appointments.CreateAppointment.NoteMessage';

export const APPOINTMENT_TRIAGE_NOTE_NN_MESSAGE_SETTING = 'Appointments.Questionnaire.TriageNoteNN';

export const CC_NHGP_TELECONSULT_VISIT_ID = ['117100003'];

export const QUERY_PARAMS_ERROR = 'error';
